import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import SearchableDropdown from '../../../../components/SearchableDropdown';
import { updateJournal, getJournalDetails, getAccountHeads, clearJournalDetails } from '../../../../redux/actions/SocietyPortal/JournalAction/JournalAction';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import { SOCIETY_PORTAL, SOCIETY_PORTAL_ACCOUNTS_JOURNAL } from '../../../../Routing/routes';

// Entry types a person may pick for a hand-written journal. The journal table's enum
// holds 16 values, but most are owned by the module that generates them — invoice,
// receipt, opening, closing, facility, purchase and so on are written by those flows and
// must not be selectable here, or a manual entry would masquerade as a system one.
// 'daily' is the enum's own default and means an ordinary journal entry; it was missing,
// so there was no way to record one.
const CATEGORIES = [
    { value: 'daily', label: 'Journal (Daily)' },
    { value: 'payment', label: 'Payment' },
    { value: 'expense', label: 'Expense' },
    { value: 'other', label: 'Other' },
];

const emptyLine = () => ({
    id: Date.now() + Math.random(),
    account_cd: '',
    debit: '',
    credit: '',
});

const inputClass = 'tw-w-full tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded tw-text-xs focus:tw-outline-none focus:tw-border-blue-500';

const EditJournal = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { journalId } = useParams();
    const { updateLoading, loading, journalDetails, accountHeads, accountHeadsLoading } = useSelector(state => state.JournalReducer);

    const societyId = selectedSocietyStorer.getSocietyId();

    const [date, setDate] = useState('');
    const [category, setCategory] = useState('payment');
    const [reffNo, setReffNo] = useState('');
    const [remarks, setRemarks] = useState('');
    const [chequeNo, setChequeNo] = useState('');
    const [clearanceDate, setClearanceDate] = useState('');
    const [lines, setLines] = useState([emptyLine(), emptyLine()]);
    const [initialized, setInitialized] = useState(false);

    useEffect(() => {
        dispatch(clearJournalDetails());
        if (societyId && journalId) {
            dispatch(getJournalDetails({ societyId, journalId }));
            dispatch(getAccountHeads({ societyId }));
        }
        return () => {
            dispatch(clearJournalDetails());
        };
    }, [dispatch, societyId, journalId]);

    // Pre-fill form when journal details load
    useEffect(() => {
        if (journalDetails && !initialized) {
            setDate(journalDetails.date || '');
            setCategory(journalDetails.entry_type || 'payment');
            setReffNo(journalDetails.reff_no || '');
            setRemarks(journalDetails.remarks || '');
            setChequeNo(journalDetails.cheque_no || '');
            setClearanceDate(journalDetails.clearance_date || '');

            const debitLines = (journalDetails.debits || []).map(d => ({
                id: d.id || Date.now() + Math.random(),
                account_cd: d.account_cd,
                debit: d.amount,
                credit: '',
            }));
            const creditLines = (journalDetails.credits || []).map(c => ({
                id: c.id || Date.now() + Math.random(),
                account_cd: c.account_cd,
                debit: '',
                credit: c.amount,
            }));
            const allLines = [...debitLines, ...creditLines];
            setLines(allLines.length >= 2 ? allLines : [...allLines, ...Array(2 - allLines.length).fill(null).map(() => emptyLine())]);
            setInitialized(true);
        }
    }, [journalDetails, initialized]);

    // Flatten accounts into dropdown options
    const accountOptions = useMemo(() => {
        if (!accountHeads?.accounts) return [];
        const options = [];
        Object.values(accountHeads.accounts).forEach(group => {
            Object.entries(group).forEach(([code, name]) => {
                options.push({ value: code, label: `${code} - ${name}` });
            });
        });
        options.sort((a, b) => a.value.localeCompare(b.value));
        return options;
    }, [accountHeads]);

    const totals = useMemo(() => {
        const totalDebit = lines.reduce((sum, l) => sum + (parseFloat(l.debit) || 0), 0);
        const totalCredit = lines.reduce((sum, l) => sum + (parseFloat(l.credit) || 0), 0);
        const difference = Math.abs(totalDebit - totalCredit);
        const isBalanced = totalDebit === totalCredit && totalDebit > 0;
        return { totalDebit, totalCredit, difference, isBalanced };
    }, [lines]);

    const hasAccountErrors = useMemo(() => {
        return lines.some(l => {
            const hasAmount = parseFloat(l.debit) > 0 || parseFloat(l.credit) > 0;
            return hasAmount && (!l.account_cd || l.account_cd === 'all');
        });
    }, [lines]);

    const formatCurrency = (amount) =>
        parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const handleLineChange = (index, field, value) => {
        setLines(prev => {
            const updated = [...prev];
            const line = { ...updated[index], [field]: value };
            if (field === 'debit' && parseFloat(value) > 0) line.credit = '';
            if (field === 'credit' && parseFloat(value) > 0) line.debit = '';
            updated[index] = line;
            return updated;
        });
    };

    const handleAddLine = () => setLines(prev => [...prev, emptyLine()]);

    const handleRemoveLine = (index) => {
        if (lines.length <= 2) return;
        setLines(prev => prev.filter((_, i) => i !== index));
    };

    const handleSave = () => {
        if (!remarks.trim() || hasAccountErrors) return;
        const debits = lines.filter(l => parseFloat(l.debit) > 0).map(l => ({
            account_cd: l.account_cd, amount: parseFloat(l.debit), add_info: '',
        }));
        const credits = lines.filter(l => parseFloat(l.credit) > 0).map(l => ({
            account_cd: l.account_cd, amount: parseFloat(l.credit), add_info: '',
        }));
        if (!debits.length || !credits.length) return;
        dispatch(updateJournal({
            societyId, journalId, date, entry_type: category, entry_from: 'journal',
            reff_no: reffNo || undefined, remarks: remarks.trim(),
            cheque_no: chequeNo || undefined, clearance_date: clearanceDate || undefined, daybook_id: 0,
            debits, credits,
            onSuccess: () => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_ACCOUNTS_JOURNAL),
        }));
    };

    const goBack = () => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_ACCOUNTS_JOURNAL);

    if (loading && !initialized) {
        return (
            <div className="tw-p-4 tw-bg-gray-50 tw-min-h-screen tw-flex tw-items-center tw-justify-center">
                <div className="tw-flex tw-items-center tw-gap-2">
                    <div className="tw-animate-spin tw-rounded-full tw-h-5 tw-w-5 tw-border-2 tw-border-gray-300 tw-border-t-blue-500"></div>
                    <span className="tw-text-gray-500 tw-text-sm">Loading journal entry...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="tw-p-4 tw-bg-gray-50 tw-min-h-screen">
            {/* Header */}
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
                <div className="tw-flex tw-items-center tw-gap-1.5">
                    <button onClick={goBack} className="tw-p-1 tw-rounded hover:tw-bg-gray-200 tw-text-gray-500 tw-flex tw-items-center tw-justify-center">
                        <Icon icon="mdi:arrow-left" className="tw-text-sm" />
                    </button>
                    <span className="tw-text-sm tw-font-semibold tw-text-gray-800">Edit Journal Entry <span className="tw-text-gray-400 tw-font-normal">#{journalId}</span></span>
                </div>
                <div className="tw-flex tw-items-center tw-gap-2">
                    <button onClick={goBack} className="tw-px-3 tw-py-1.5 tw-rounded tw-text-xs tw-font-medium tw-text-gray-700 tw-bg-white tw-border tw-border-gray-200 hover:tw-bg-gray-50">
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={updateLoading || !totals.isBalanced || !remarks.trim() || hasAccountErrors}
                        className="tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1.5 tw-rounded tw-text-xs tw-font-medium tw-text-white tw-bg-red-600 hover:tw-bg-red-700 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                    >
                        {updateLoading ? (
                            <div className="tw-animate-spin tw-rounded-full tw-h-3 tw-w-3 tw-border-2 tw-border-white tw-border-t-transparent"></div>
                        ) : (
                            <Icon icon="mdi:check" className="tw-text-xs" />
                        )}
                        Update & Post
                    </button>
                </div>
            </div>

            {/* Form Fields */}
            <div className="tw-bg-white tw-rounded-lg tw-p-3 tw-mb-3 tw-shadow-sm tw-border tw-border-gray-100">
                <div className="tw-grid tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-3">
                    <div>
                        <label className="tw-flex tw-items-center tw-gap-1 tw-text-[10px] tw-text-gray-700 tw-font-medium tw-uppercase tw-tracking-wide tw-mb-1"><Icon icon="mdi:calendar" className="tw-text-xs" /> Date <span className="tw-text-red-500">*</span></label>
                        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputClass} />
                    </div>
                    <div>
                        <label className="tw-flex tw-items-center tw-gap-1 tw-text-[10px] tw-text-gray-700 tw-font-medium tw-uppercase tw-tracking-wide tw-mb-1"><Icon icon="mdi:tag-outline" className="tw-text-xs" /> Entry Type <span className="tw-text-red-500">*</span></label>
                        <SearchableDropdown value={category} onChange={setCategory} options={CATEGORIES} placeholder="Select entry type" valueKey="value" labelKey="label" clearable={false} small />
                    </div>
                    <div>
                        <label className="tw-flex tw-items-center tw-gap-1 tw-text-[10px] tw-text-gray-700 tw-font-medium tw-uppercase tw-tracking-wide tw-mb-1"><Icon icon="mdi:file-document-outline" className="tw-text-xs" /> Reference No.</label>
                        <input type="text" value={reffNo} onChange={(e) => setReffNo(e.target.value)} placeholder="e.g. INS-001" className={inputClass} />
                    </div>
                    <div>
                        <label className="tw-flex tw-items-center tw-gap-1 tw-text-[10px] tw-text-gray-700 tw-font-medium tw-uppercase tw-tracking-wide tw-mb-1"><Icon icon="mdi:comment-text-outline" className="tw-text-xs" /> Remarks <span className="tw-text-red-500">*</span></label>
                        <input type="text" value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Enter remarks" className={inputClass} />
                    </div>
                    <div>
                        <label className="tw-flex tw-items-center tw-gap-1 tw-text-[10px] tw-text-gray-700 tw-font-medium tw-uppercase tw-tracking-wide tw-mb-1"><Icon icon="mdi:checkbook" className="tw-text-xs" /> Cheque No.</label>
                        <input type="text" value={chequeNo} onChange={(e) => setChequeNo(e.target.value)} placeholder="e.g. 123456" className={inputClass} />
                    </div>
                    <div>
                        <label className="tw-flex tw-items-center tw-gap-1 tw-text-[10px] tw-text-gray-700 tw-font-medium tw-uppercase tw-tracking-wide tw-mb-1"><Icon icon="mdi:calendar-check" className="tw-text-xs" /> Clearance Date</label>
                        <input type="date" value={clearanceDate} onChange={(e) => setClearanceDate(e.target.value)} className={inputClass} />
                    </div>
                </div>
            </div>

            {/* Journal Lines */}
            <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100">
                <div className="tw-flex tw-items-center tw-justify-between tw-px-3 tw-py-2 tw-border-b tw-border-gray-100">
                    <h2 className="tw-text-xs tw-font-semibold tw-text-gray-800">Journal Lines</h2>
                    <span className="tw-text-[10px] tw-text-gray-400">{lines.length} lines</span>
                </div>

                <table className="tw-w-full tw-text-xs">
                    <thead>
                        <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-100">
                            <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase tw-w-[36px]">#</th>
                            <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase">Account <span className="tw-text-red-500">*</span></th>
                            <th className="tw-text-right tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase tw-w-[120px]">Debit (₹)</th>
                            <th className="tw-text-right tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase tw-w-[120px]">Credit (₹)</th>
                            <th className="tw-text-center tw-px-2 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase tw-w-[40px]"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {lines.map((line, index) => (
                            <tr key={line.id} className="tw-border-b tw-border-gray-50">
                                <td className="tw-px-3 tw-py-1.5"><span className="tw-text-gray-400">{index + 1}</span></td>
                                <td className="tw-px-3 tw-py-1.5">
                                    <SearchableDropdown
                                        value={line.account_cd}
                                        onChange={(val) => handleLineChange(index, 'account_cd', val)}
                                        options={accountOptions}
                                        placeholder="Select account"
                                        valueKey="value"
                                        labelKey="label"
                                        loading={accountHeadsLoading}
                                        clearable={false}
                                        small
                                    />
                                </td>
                                <td className="tw-px-3 tw-py-1.5">
                                    <input type="number" value={line.debit} onChange={(e) => handleLineChange(index, 'debit', e.target.value)} placeholder="0.00" min="0" step="0.01" disabled={parseFloat(line.credit) > 0} className={`${inputClass} tw-text-right ${parseFloat(line.credit) > 0 ? 'tw-bg-gray-50 tw-text-gray-400' : ''}`} />
                                </td>
                                <td className="tw-px-3 tw-py-1.5">
                                    <input type="number" value={line.credit} onChange={(e) => handleLineChange(index, 'credit', e.target.value)} placeholder="0.00" min="0" step="0.01" disabled={parseFloat(line.debit) > 0} className={`${inputClass} tw-text-right ${parseFloat(line.debit) > 0 ? 'tw-bg-gray-50 tw-text-gray-400' : ''}`} />
                                </td>
                                <td className="tw-px-2 tw-py-1.5 tw-text-center">
                                    <button onClick={() => handleRemoveLine(index)} disabled={lines.length <= 2} className="tw-p-1 tw-rounded hover:tw-bg-red-50 tw-text-gray-400 hover:tw-text-red-500 disabled:tw-opacity-30 disabled:tw-cursor-not-allowed" title="Remove">
                                        <Icon icon="mdi:trash-can-outline" className="tw-text-xs" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <div className="tw-px-3 tw-py-2 tw-border-t tw-border-gray-100">
                    <button onClick={handleAddLine} className="tw-flex tw-items-center tw-gap-1 tw-text-blue-600 tw-text-xs tw-font-medium hover:tw-text-blue-700">
                        <Icon icon="mdi:plus-circle-outline" className="tw-text-sm" />
                        Add Line
                    </button>
                </div>
            </div>

            {/* Summary */}
            <div className="tw-bg-white tw-rounded-lg tw-mt-3 tw-shadow-sm tw-border tw-border-gray-100">
                <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3">
                    <div className="tw-flex tw-items-center tw-gap-6">
                        <div>
                            <p className="tw-text-[10px] tw-text-gray-500 tw-uppercase tw-tracking-wide">Total Debit</p>
                            <p className="tw-text-sm tw-font-semibold tw-text-emerald-600 tw-mt-0.5">₹{formatCurrency(totals.totalDebit)}</p>
                        </div>
                        <div className="tw-w-px tw-h-8 tw-bg-gray-200"></div>
                        <div>
                            <p className="tw-text-[10px] tw-text-gray-500 tw-uppercase tw-tracking-wide">Total Credit</p>
                            <p className="tw-text-sm tw-font-semibold tw-text-red-600 tw-mt-0.5">₹{formatCurrency(totals.totalCredit)}</p>
                        </div>
                        <div className="tw-w-px tw-h-8 tw-bg-gray-200"></div>
                        <div>
                            <p className="tw-text-[10px] tw-text-gray-500 tw-uppercase tw-tracking-wide">Difference</p>
                            <p className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-mt-0.5">₹{formatCurrency(totals.difference)}</p>
                        </div>
                    </div>
                    {totals.isBalanced ? (
                        <span className="tw-inline-flex tw-items-center tw-gap-1 tw-bg-emerald-50 tw-text-emerald-700 tw-text-[10px] tw-font-medium tw-px-2 tw-py-0.5 tw-rounded-full">
                            <Icon icon="mdi:check-circle" className="tw-text-xs" /> Balanced
                        </span>
                    ) : (
                        <span className="tw-inline-flex tw-items-center tw-gap-1 tw-bg-red-50 tw-text-red-600 tw-text-[10px] tw-font-medium tw-px-2 tw-py-0.5 tw-rounded-full">
                            <Icon icon="mdi:alert-circle" className="tw-text-xs" /> Unbalanced
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EditJournal;
